import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { Card, SectionTitle } from '@/components/Card';
import { Badge, type BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Spinner } from '@/components/LoadingScreen';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useBusinessUnits } from '@/app/hooks';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import { useOperationalAccess } from '@/app/useOperationalAccess';
import { ApiRequestError } from '@/services/api';
import { inventoryService } from '@/services/operational.service';
import {
  INVENTORY_STATUS_TITLES,
  type InventoryDocument,
  type InventoryStatus,
} from '@/types/operational';
import { formatAmount, formatByCurrency, formatDate, formatQuantity } from '@/features/operational/money';
import { v } from '@/theme/tokens';

const STATUS_TONE: Record<InventoryStatus, BadgeTone> = {
  DRAFT: 'neutral',
  VALIDATED: 'accent',
  NEEDS_REVIEW: 'warning',
  CONFIRMED: 'positive',
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Инвентаризация.
 *
 * The page is the workflow, in order: upload → preview → confirm. It never
 * collapses them — an uploaded file lands as a draft with its findings on
 * screen, and the confirm button is the only thing that records anything.
 */
export function InventoryPage() {
  const access = useOperationalAccess();
  const wide = useIsWideScreen();
  const client = useQueryClient();
  const units = useBusinessUnits();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filterUnit, setFilterUnit] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pendingCorrection, setPendingCorrection] = useState<InventoryDocument | null>(null);

  const reference = useQuery({
    queryKey: ['operational', 'inventory', 'reference'],
    queryFn: ({ signal }) => inventoryService.reference(signal),
    enabled: access.can('INVENTORY'),
    staleTime: 10 * 60_000,
  });

  const documents = useQuery({
    queryKey: ['operational', 'inventory', 'documents', filterUnit, filterZone],
    queryFn: ({ signal }) =>
      inventoryService.documents(
        { business_unit_id: filterUnit || undefined, zone: filterZone || undefined },
        signal,
      ),
    enabled: access.can('INVENTORY'),
  });

  const detail = useQuery({
    queryKey: ['operational', 'inventory', 'document', selectedId],
    queryFn: ({ signal }) => inventoryService.document(selectedId as string, signal),
    enabled: Boolean(selectedId),
  });

  const comparison = useQuery({
    queryKey: ['operational', 'inventory', 'comparison', selectedId],
    queryFn: ({ signal }) => inventoryService.comparison(selectedId as string, signal),
    enabled: Boolean(selectedId) && detail.data?.status === 'CONFIRMED',
  });

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['operational'] });
  };

  const fail = (fallback: string) => (e: unknown) =>
    setError(e instanceof ApiRequestError ? e.message : fallback);

  const upload = useMutation({
    mutationFn: inventoryService.upload,
    onSuccess: (data) => {
      setError(null);
      setSelectedId(data.id);
      refresh();
    },
    onError: fail('Не удалось загрузить файл'),
  });

  const confirm = useMutation({
    mutationFn: (id: string) =>
      inventoryService.confirm(id, { createNewItems: true, acknowledgeWarnings: true }),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: fail('Не удалось подтвердить инвентаризацию'),
  });

  const correction = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      inventoryService.correction(id, reason),
    onSuccess: (data) => {
      setPendingCorrection(null);
      setError(null);
      setSelectedId(data.id);
      refresh();
    },
    onError: fail('Не удалось создать корректировку'),
  });

  if (access.loading) return <Spinner />;
  if (!access.can('INVENTORY')) {
    return (
      <>
        <PageHeader title="Инвентаризация" />
        <EmptyState
          icon="lock"
          title="Модуль недоступен"
          description="Доступ к инвентаризации выдаёт владелец."
        />
      </>
    );
  }

  const canUpload = access.can('INVENTORY', 'UPLOAD');
  const canManage = access.can('INVENTORY', 'MANAGE');
  const doc = detail.data;

  return (
    <>
      <PageHeader
        title="Инвентаризация"
        subtitle="Загрузка, проверка и подтверждение фактических остатков"
      />

      {error && (
        <div
          role="alert"
          style={{
            background: v('negative-soft'),
            color: v('negative'),
            padding: '9px 12px',
            borderRadius: 10,
            fontSize: 13,
            marginBottom: 14,
          }}
        >
          {error}
        </div>
      )}

      {canUpload && (
        <UploadCard
          units={units.data ?? []}
          zones={reference.data?.zones ?? []}
          busy={upload.isPending}
          onUpload={(input) => upload.mutate(input)}
        />
      )}

      <Card style={{ marginBottom: 18 }}>
        <SectionTitle right={<Badge tone="neutral">{documents.data?.length ?? 0}</Badge>}>
          История инвентаризаций
        </SectionTitle>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <select
            aria-label="Объект"
            value={filterUnit}
            onChange={(event) => setFilterUnit(event.target.value)}
            style={selectStyle}
          >
            <option value="">Все объекты</option>
            {(units.data ?? []).map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </select>
          <select
            aria-label="Зона"
            value={filterZone}
            onChange={(event) => setFilterZone(event.target.value)}
            style={selectStyle}
          >
            <option value="">Все зоны</option>
            {(reference.data?.zones ?? []).map((zone) => (
              <option key={zone.code} value={zone.code}>
                {zone.title}
              </option>
            ))}
          </select>
        </div>

        {documents.isPending && <Spinner />}
        {documents.error && (
          <ErrorState
            message={documents.error instanceof Error ? documents.error.message : 'Ошибка'}
            onRetry={() => void documents.refetch()}
          />
        )}
        {documents.data?.length === 0 && (
          <div style={{ fontSize: 13, color: v('ink-muted') }}>Инвентаризаций пока нет.</div>
        )}

        {(documents.data ?? []).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setSelectedId(item.id)}
            style={{
              display: 'flex',
              width: '100%',
              textAlign: 'left',
              gap: 12,
              padding: '11px 0',
              borderTop: `1px solid ${v('line-soft')}`,
              background: 'transparent',
              border: 'none',
              borderTopStyle: 'solid',
              cursor: 'pointer',
              alignItems: wide ? 'center' : 'flex-start',
              flexDirection: wide ? 'row' : 'column',
              color: v('ink'),
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                {item.business_unit_name} · {item.zone_title}
              </div>
              <div style={{ fontSize: 12, color: v('ink-muted') }}>
                {formatDate(item.inventory_date)} · позиций: {item.line_count} ·{' '}
                {formatByCurrency(item.totals_by_currency).join(' · ')}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {item.superseded_at && <Badge tone="neutral">Исправлена</Badge>}
              {item.corrects_document_id && <Badge tone="accent">Корректировка</Badge>}
              {item.error_count > 0 && <Badge tone="negative">Ошибок: {item.error_count}</Badge>}
              {item.warning_count > 0 && (
                <Badge tone="warning">Предупреждений: {item.warning_count}</Badge>
              )}
              <Badge tone={STATUS_TONE[item.status]}>
                {INVENTORY_STATUS_TITLES[item.status]}
              </Badge>
            </div>
          </button>
        ))}
      </Card>

      {selectedId && detail.isPending && <Spinner />}

      {doc && (
        <>
          <Card style={{ marginBottom: 18 }}>
            <SectionTitle
              right={
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {doc.status !== 'CONFIRMED' && canManage && (
                    <Button
                      variant="primary"
                      disabled={!doc.can_confirm || confirm.isPending}
                      style={{ padding: '6px 12px', fontSize: 12.5 }}
                      onClick={() => confirm.mutate(doc.id)}
                    >
                      {confirm.isPending ? 'Подтверждение…' : 'Подтвердить'}
                    </Button>
                  )}
                  {doc.status === 'CONFIRMED' && canManage && !doc.superseded_at && (
                    <Button
                      style={{ padding: '6px 12px', fontSize: 12.5 }}
                      onClick={() => setPendingCorrection(doc)}
                    >
                      Создать корректировку
                    </Button>
                  )}
                  <Button
                    style={{ padding: '6px 12px', fontSize: 12.5 }}
                    onClick={() => setSelectedId(null)}
                  >
                    Закрыть
                  </Button>
                </div>
              }
            >
              {doc.business_unit_name} · {doc.zone_title} · {formatDate(doc.inventory_date)}
            </SectionTitle>

            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 14 }}>
              <Metric label="Позиций" value={String(doc.line_count)} />
              <Metric
                label="Стоимость запасов"
                value={formatByCurrency(doc.totals_by_currency).join(' · ')}
              />
              <Metric label="Ошибок" value={String(doc.error_count)} tone={doc.error_count ? 'negative' : undefined} />
              <Metric
                label="Предупреждений"
                value={String(doc.warning_count)}
                tone={doc.warning_count ? 'warning' : undefined}
              />
            </div>

            {doc.status === 'CONFIRMED' && (
              <div style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 10 }}>
                Подтверждена {formatDate(doc.confirmed_at)}. Подтверждённая инвентаризация не
                изменяется — исправление оформляется отдельной корректировкой.
              </div>
            )}
            {doc.status !== 'CONFIRMED' && doc.error_count > 0 && (
              <div style={{ fontSize: 12.5, color: v('negative'), marginBottom: 10 }}>
                Есть ошибки — исправьте их в файле и загрузите его заново. Система не исправляет
                строки самостоятельно.
              </div>
            )}
            {doc.new_items.length > 0 && doc.status !== 'CONFIRMED' && (
              <div style={{ fontSize: 12.5, color: v('ink-sub'), marginBottom: 10 }}>
                Новых позиций: <strong>{doc.new_items.length}</strong> — при подтверждении они
                будут добавлены в справочник: {doc.new_items.slice(0, 8).join(', ')}
                {doc.new_items.length > 8 ? '…' : ''}
              </div>
            )}

            {doc.issues.length > 0 && <IssueList issues={doc.issues} />}
          </Card>

          <Card style={{ marginBottom: 18 }}>
            <SectionTitle>Строки</SectionTitle>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: v('ink-muted'), fontSize: 11.5 }}>
                    <th style={{ padding: '6px 8px 6px 0' }}>Позиция</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Количество</th>
                    <th style={{ padding: '6px 8px' }}>Ед.</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right' }}>Цена</th>
                    <th style={{ padding: '6px 0 6px 8px', textAlign: 'right' }}>Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {doc.lines.map((line) => (
                    <tr key={line.id} style={{ borderTop: `1px solid ${v('line-soft')}` }}>
                      <td style={{ padding: '7px 8px 7px 0' }}>
                        {line.name}
                        {!line.matched && (
                          <span style={{ marginLeft: 6 }}>
                            <Badge tone="warning">новая</Badge>
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatQuantity(line.quantity)}
                      </td>
                      <td style={{ padding: '7px 8px', color: v('ink-muted') }}>
                        {line.unit_title}
                      </td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatAmount(line.unit_price, line.currency)}
                      </td>
                      <td style={{ padding: '7px 0 7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatAmount(line.total_value, line.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {doc.status === 'CONFIRMED' && comparison.data && (
            <ComparisonCard data={comparison.data} />
          )}
        </>
      )}

      {pendingCorrection && (
        <CorrectionDialog
          busy={correction.isPending}
          onCancel={() => setPendingCorrection(null)}
          onConfirm={(reason) => correction.mutate({ id: pendingCorrection.id, reason })}
        />
      )}
    </>
  );
}

const selectStyle = {
  padding: '7px 10px',
  border: `1px solid ${v('line')}`,
  borderRadius: 9,
  background: v('surface'),
  color: v('ink'),
  fontSize: 12.5,
  maxWidth: '100%',
} as const;

function Metric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'negative' | 'warning';
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11.5, color: v('ink-muted') }}>{label}</div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: tone === 'negative' ? v('negative') : tone === 'warning' ? v('warning') : v('ink'),
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </div>
    </div>
  );
}

/** Errors first, then warnings — the blocking ones are what to act on. */
function IssueList({ issues }: { issues: { severity: string; code: string; message: string; row_number?: number | null; name: string }[] }) {
  const errors = issues.filter((i) => i.severity === 'ERROR');
  const warnings = issues.filter((i) => i.severity === 'WARNING');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {[...errors, ...warnings].map((issue, index) => (
        <div
          key={`${issue.code}-${issue.row_number ?? index}-${index}`}
          role={issue.severity === 'ERROR' ? 'alert' : undefined}
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
            background: issue.severity === 'ERROR' ? v('negative-soft') : v('warning-soft'),
            color: issue.severity === 'ERROR' ? v('negative') : v('warning'),
            padding: '7px 10px',
            borderRadius: 9,
            fontSize: 12.5,
          }}
        >
          {issue.row_number != null && (
            <span style={{ fontWeight: 700, flexShrink: 0 }}>стр. {issue.row_number}</span>
          )}
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {issue.name ? `${issue.name}: ` : ''}
            {issue.message}
          </span>
        </div>
      ))}
    </div>
  );
}

function UploadCard({
  units,
  zones,
  busy,
  onUpload,
}: {
  units: { id: string; name: string }[];
  zones: { code: string; title: string }[];
  busy: boolean;
  onUpload: (input: {
    file: File;
    businessUnitId: string;
    zone: string;
    inventoryDate: string;
  }) => void;
}) {
  const [unitId, setUnitId] = useState('');
  const [zone, setZone] = useState('');
  const [when, setWhen] = useState(todayIso());
  const [file, setFile] = useState<File | null>(null);

  const ready = Boolean(unitId && zone && when && file);

  return (
    <Card style={{ marginBottom: 18 }}>
      <SectionTitle>Загрузить инвентаризацию</SectionTitle>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Объект</span>
          <select
            aria-label="Объект инвентаризации"
            value={unitId}
            onChange={(event) => setUnitId(event.target.value)}
            style={selectStyle}
          >
            <option value="">Выберите…</option>
            {units.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Зона</span>
          <select
            aria-label="Зона"
            value={zone}
            onChange={(event) => setZone(event.target.value)}
            style={selectStyle}
          >
            <option value="">Выберите…</option>
            {zones.map((item) => (
              <option key={item.code} value={item.code}>
                {item.title}
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Дата</span>
          <input
            type="date"
            aria-label="Дата инвентаризации"
            value={when}
            onChange={(event) => setWhen(event.target.value)}
            style={selectStyle}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
          <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Файл (xlsx / csv)</span>
          <input
            type="file"
            aria-label="Файл инвентаризации"
            accept=".xlsx,.xlsm,.csv"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            style={{ ...selectStyle, padding: '6px 8px' }}
          />
        </label>

        <Button
          variant="primary"
          disabled={!ready || busy}
          onClick={() => {
            if (!file) return;
            onUpload({ file, businessUnitId: unitId, zone, inventoryDate: when });
          }}
        >
          {busy ? 'Загрузка…' : 'Загрузить'}
        </Button>
      </div>
      <p style={{ fontSize: 11.5, color: v('ink-faint'), marginTop: 10, marginBottom: 0 }}>
        Загрузка не записывает данные. Файл проверяется, и вы увидите предпросмотр с ошибками и
        предупреждениями — инвентаризация становится фактом только после подтверждения.
      </p>
    </Card>
  );
}

/**
 * Two counts side by side.
 *
 * The header deliberately says «изменение фактического остатка» and not
 * «недостача»: a shortage is actual against theoretical, and this system has
 * no theoretical stock until a POS supplies one.
 */
function ComparisonCard({
  data,
}: {
  data: {
    previous: { inventory_date: string } | null;
    rows: {
      name: string;
      unit: string;
      unit_title: string;
      previous_unit: string | null;
      previous_unit_title: string | null;
      current_quantity: string | null;
      previous_quantity: string | null;
      delta_quantity: string | null;
      current_value: string | null;
      previous_value: string | null;
      delta_value: string | null;
      currency: string;
      is_new: boolean;
      is_missing: boolean;
      unit_changed: boolean;
    }[];
    new_item_count: number;
    missing_item_count: number;
    delta_by_currency: Record<string, string>;
    incomparable_count: number;
  };
}) {
  if (!data.previous) {
    return (
      <Card>
        <SectionTitle>Сравнение</SectionTitle>
        <div style={{ fontSize: 13, color: v('ink-muted') }}>
          Это первая подтверждённая инвентаризация по этому объекту и зоне — сравнивать пока не с
          чем.
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <SectionTitle
        right={
          <span style={{ fontSize: 12, color: v('ink-muted') }}>
            предыдущая: {formatDate(data.previous.inventory_date)}
          </span>
        }
      >
        Изменение фактического остатка
      </SectionTitle>

      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginBottom: 12 }}>
        <Metric
          label="Изменение стоимости"
          value={formatByCurrency(data.delta_by_currency, { signed: true }).join(' · ')}
        />
        <Metric label="Новые позиции" value={String(data.new_item_count)} />
        <Metric label="Исчезнувшие позиции" value={String(data.missing_item_count)} />
        {data.incomparable_count > 0 && (
          <Metric
            label="Без пересчёта единиц"
            value={String(data.incomparable_count)}
            tone="warning"
          />
        )}
      </div>

      <p style={{ fontSize: 11.5, color: v('ink-faint'), marginTop: 0, marginBottom: 12 }}>
        Это изменение фактического остатка между двумя пересчётами, а не недостача: теоретический
        остаток появится только после подключения складского учёта.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: v('ink-muted'), fontSize: 11.5 }}>
              <th style={{ padding: '6px 8px 6px 0' }}>Позиция</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Было</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Стало</th>
              <th style={{ padding: '6px 8px', textAlign: 'right' }}>Δ кол-во</th>
              <th style={{ padding: '6px 0 6px 8px', textAlign: 'right' }}>Δ стоимость</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.name} style={{ borderTop: `1px solid ${v('line-soft')}` }}>
                <td style={{ padding: '7px 8px 7px 0' }}>
                  {row.name}
                  {row.is_new && (
                    <span style={{ marginLeft: 6 }}>
                      <Badge tone="accent">новая</Badge>
                    </span>
                  )}
                  {row.is_missing && (
                    <span style={{ marginLeft: 6 }}>
                      <Badge tone="warning">исчезла</Badge>
                    </span>
                  )}
                </td>
                <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {formatQuantity(row.previous_quantity)} {row.previous_unit_title ?? ''}
                </td>
                <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {formatQuantity(row.current_quantity)}{' '}
                  {row.current_quantity ? row.unit_title : ''}
                </td>
                <td style={{ padding: '7px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {row.unit_changed ? (
                    <span style={{ color: v('warning'), fontSize: 11.5 }}>
                      единица изменилась
                    </span>
                  ) : (
                    formatQuantity(row.delta_quantity)
                  )}
                </td>
                <td
                  style={{
                    padding: '7px 0 7px 8px',
                    textAlign: 'right',
                    whiteSpace: 'nowrap',
                    color:
                      row.delta_value && Number(row.delta_value) < 0
                        ? v('negative')
                        : v('ink'),
                  }}
                >
                  {row.delta_value ? formatAmount(row.delta_value, row.currency) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CorrectionDialog({
  busy,
  onCancel,
  onConfirm,
}: {
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <ConfirmDialog
      title="Создать корректировку"
      message="Подтверждённая инвентаризация не изменяется. Будет создан новый черновик с теми же строками — исправьте его и подтвердите; исходная инвентаризация останется в истории."
      confirmLabel="Создать корректировку"
      busy={busy}
      onCancel={onCancel}
      onConfirm={() => onConfirm(reason.trim() || 'Корректировка')}
    >
      <label style={{ display: 'block', marginTop: 12 }}>
        <span style={{ fontSize: 11.5, color: v('ink-muted') }}>Причина</span>
        <input
          aria-label="Причина корректировки"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Например: пересчитали бар"
          style={{ ...selectStyle, width: '100%', marginTop: 4 }}
        />
      </label>
    </ConfirmDialog>
  );
}
